/**
 * All functions that write data to the API (DELETE, POST, PUT requests should go here)
 */
import axios, { AxiosResponse } from "axios";
import { handleSessionExpiration } from "./sessionManagement";
import { Labels } from "../components/sharedComponents/PatientLabels";

/**
 * Does the request to edit the clinician like specified in the given parameter object
 * @param clinicianData
 * @returns
 */
export async function editClinician(clinicianData: ClinicianData) {
  const url = "/api/v2/auth/user/clinician";
  const response = await genericPatchWithRetry(url, clinicianData);

  return response.data;
}

/**
 * Assigns a patient to another clinician. Only usable as an admin. When signed in as clinician use the other function which is made for clinicians.
 * @param assignmentData
 * @returns
 */
export async function adminAssignClinician(
  assignmentData: ClinicianAssignmentData,
) {
  const url = "/api/v2/auth/patient/assignment";
  const response = await genericPostWithRetry(url, assignmentData);

  return response.data;
}

/**
 * Assigns a patient ot another clinician. Only usable as clinician. When signed in as admin use the respective function for admins instead.
 * @param assignmentData
 * @returns
 */
export async function clinicianAssignClinician(
  assignmentData: ClinicianAssignmentData,
) {
  const url = "/api/v2/auth/patient/assignment";
  const response = await genericPostWithRetry(url, assignmentData);

  return response.data;
}

/**
 * Edits a patient according to the data given in the parameters. Only usable as clinician.
 * @param patientData
 * @returns
 */
export async function editPatient(
  patientData: PatientData,
): Promise<SurveyList> {
  const url = "/api/v2/auth/patient";
  const response = await genericPostWithRetry(url, patientData);

  return response.data;
}

/**
 * Saves the given survey data to the server. Typically used when a patient is answering questions in the survey.
 * @param surveyData
 * @returns
 */
export async function setSurvey(surveyData: SurveyData) {
  const url = "/api/v2/auth/survey/items";
  const serializedData: SerializedData = {
    type: "json",
    value: JSON.stringify(surveyData),
  };
  const response = await genericPostWithRetry(url, serializedData);
  return response.data;
}

/**
 * Sets the password of the currently signed in clinician to the value specified in the parameter object.
 * Note that the parameter object contains the password twice (for confirmation) and that it also has to contain
 * the old password.
 * @param passwordData
 * @returns
 */
export async function setPassword(passwordData: PasswordData) {
  const url = "/api/v2/auth/user/password";
  const response = await genericPostWithRetry(url, passwordData);

  return response.data;
}

/**
 * Resets the password of the clinician with the given id and returns the newly generated password.
 * Only usable when signed in as admin
 * @param clinicianId
 * @returns
 */
export async function adminResetClinicianPassword(clinicianId: string) {
  // cast clinicianID string to ClinicianID Interface
  const clinician_id: ClinicianID = { clinician_id: clinicianId };

  const url = "/api/v2/auth/user/password/reset";
  const response = await genericPostWithRetry(url, clinician_id);

  return response.data;
}

/**
 * Activates the clinician with the given id.
 * @param clinicianId
 * @returns
 */
export async function activateClinician(clinicianId: number): Promise<void> {
  // cast clinicianID string to ClinicianID Interface
  const clinician_id: ClinicianID = { clinician_id: clinicianId.toString() };

  const url = "/api/v2/auth/user/clinician/activation";
  const response = await genericPostWithRetry(url, clinician_id);

  return response.data;
}

/**
 * Deactivates the clinician with the given id.
 * @param clinicianId
 * @returns
 */
export async function deactivateClinician(clinicianId: number): Promise<void> {
  // cast clinicianID string to ClinicianID Interface
  const clinician_id: ClinicianID = { clinician_id: clinicianId.toString() };

  const url = "/api/v2/auth/user/clinician/deactivation";
  const response = await genericPostWithRetry(url, clinician_id);

  return response.data;
}

/**
 * Generic user registration function
 * @param userData - Registration data
 * @param userType - Type of user to register
 */
export async function registerUser(
  userData: UserRegistrationData,
  userType: "patient" | "clinician" | "admin",
) {
  const urlMap = {
    patient: "/api/v2/auth/user/patient",
    clinician: "/api/v2/auth/user/clinician",
    admin: "/api/v2/auth/user/clinic-admin",
  };

  return await genericPostWithRetry(urlMap[userType], userData);
}

/**
 * This function seems to be redundant with adminAssignClinician. Right now it exists for legacy reasons until we are
 * sure why there were two functions in the first place.
 * Transfers the patient with the given code from the given clinician to the given clinician.
 * Only usable when signed in as admin.
 * Notice that for some reason the api requires the fromClinician value - I don't really know why
 * @param patientCode
 * @param clinicianFrom
 * @param clinicianTo
 * @deprecated
 * @returns
 */
export async function transferPatient(
  patientCode: string,
  clinicianFrom: string,
  clinicianTo: string,
): Promise<void> {
  const url = "/api/v2/auth/patient/assignment";

  const transferData = {
    clinician_from: clinicianFrom,
    clinician_to: clinicianTo,
    patient_id: patientCode,
  };

  await genericPostWithRetry(url, transferData);

  return;
}

/**
 * Transfers multiple patients from one clinician to another
 * @param patientCodes
 * @param clinicianFrom
 * @param clinicianTo
 * @returns
 */
export async function transferPatients(
  patientCodes: string[],
  clinicianFrom: string,
  clinicianTo: string,
): Promise<void> {
  const promises = patientCodes.map((patientCode) => {
    return transferPatient(patientCode, clinicianFrom, clinicianTo);
  });

  await Promise.all(promises);
  return;
}

/**
 * Sends the given file object to the bulk import endpoint to import multiple patients at once
 * @param file {file} CSV file
 */
export async function bulkImport(file) {
  const url = "/api/v2/auth/user/patient/csv";
  const formData = new FormData();
  formData.append("file", file);

  return await genericPostWithRetry(url, formData, {});
}

/**
 * Sends the given file object to the bulk validate endpoint to validate multiple patients at once
 * @param file {file} CSV file
 */
export async function bulkValidate(file) {
  const url = "/api/v2/auth/user/patient/csv/validation";
  const formData = new FormData();
  formData.append("file", file);

  return await genericPostWithRetry(url, formData, {});
}

type RequestData =
  | ClinicianData
  | ClinicianAssignmentData
  | PatientData
  | UserRegistrationData
  | SurveyData
  | PasswordData
  | FormData
  | SerializedData
  | ClinicianID;

interface SerializedData {
  type: "json";
  value: string;
}

/**
 * This is a generic function used to do http post requests, setting additional headers if needed.
 * If it gets a 403 status code the first time (=token invalid) it triggers
 * a renew of the token and tries to do the original request again. If that second try fails too then the
 * error is passed along. Other errors than status code 403 are always passed along.
 *
 * @param url
 * @param data the http request body
 * @param header option for additional headers
 */
async function genericPostWithRetry(
  url: string,
  data: RequestData,
  header: Record<string, string> = { "Content-Type": "application/json" },
): Promise<AxiosResponse> {
  const requestData =
    (data as SerializedData).type === "json"
      ? (data as SerializedData).value
      : data;

  return await handleSessionExpiration(async () => {
    return await axios.post(url, requestData, {
      withCredentials: true,
      headers: header,
    });
  });
}

async function genericPatchWithRetry(
  url: string,
  data: RequestData,
  header: Record<string, string> = { "Content-Type": "application/json" },
): Promise<AxiosResponse> {
  const requestData =
    (data as SerializedData).type === "json"
      ? (data as SerializedData).value
      : data;

  return await handleSessionExpiration(async () => {
    return await axios.patch(url, requestData, {
      withCredentials: true,
      headers: header,
    });
  });
}

interface ClinicianData {
  enabled: boolean;
  id: string;
  username: string;
}

interface ClinicianAssignmentData {
  patient_id: string;
  clinician_from: string;
  clinician_to: string;
}

interface PatientData {
  date_of_birth: string;
  admission: string;
  discharge: string;
  clinician_id: string;
  clinician_username: string;
  diagnoses: unknown;
  id: string;
  gender: string;
  code: string;
  labels: Labels;
  last_update: number;
}

type SurveyData = ItemResponse[];

interface ItemResponse {
  id: number;
  label: string;
  time_taken: number;
  value: string;
}

interface PasswordData {
  new_password: string;
  old_password: string;
  confirmed_new_password: string; //this is the field where the new password has to be entered a second time
}

interface ClinicianID {
  clinician_id: string;
}

interface SurveyList {
  surveys: SurveyPost;
}

export interface SurveyPost {
  id: number;
  stw_type: string;
  started_time: string;
  updated_time: string;
  finished: boolean;
  discarded: boolean;
}

type UserRegistrationData =
  PatientRegistrationData | ClinicianRegistrationData | AdminRegistrationData;

export interface PatientRegistrationData {
  code: string;
  day_of_birth: string;
  gender: string;
  clinician_username: string;
  admission: string;
  diagnosis_ids: string[];
  discharge: string;
  labels: Labels;
}

export interface ClinicianRegistrationData {
  username: string;
}

export interface AdminRegistrationData {
  clinic_id: string;
  username: string;
}
